import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  FaPhone, FaEnvelope, FaMapMarkerAlt, FaWhatsapp, 
  FaUser, FaCalendarAlt, FaUsers, FaPaperPlane 
} from 'react-icons/fa';

const Contact = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    travelers: '',
    dates: '',
    destination: '',
    message: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, this would send data to a backend
    const message = `Hello! I'm interested in booking a caravan.%0A%0AName: ${formData.name}%0AEmail: ${formData.email}%0APhone: ${formData.phone}%0ANumber of Travelers: ${formData.travelers}%0ATravel Dates: ${formData.dates}%0ADestination: ${formData.destination}%0A%0AMessage: ${formData.message}`;
    window.open(`https://wa.me/918380866111?text=${message}`, '_blank');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactInfo = [
    {
      icon: <FaPhone />,
      title: 'Call Us',
      details: '+91 83808-66111',
      link: 'tel:+918380866111',
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: <FaWhatsapp />,
      title: 'WhatsApp',
      details: 'Quick Response',
      link: 'https://wa.me/918380866111',
      color: 'from-green-400 to-green-600',
    },
    {
      icon: <FaEnvelope />,
      title: 'Email',
      details: 'info@wowcaravans.com',
      link: 'mailto:info@wowcaravans.com',
      color: 'from-red-400 to-red-600',
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Location',
      details: 'Nagpur, Maharashtra',
      link: 'https://goo.gl/maps',
      color: 'from-purple-400 to-purple-600',
    },
  ];

  return (
    <section id="contact" className="section-padding bg-gradient-to-b from-primary-50 to-white">
      <div className="container-custom">
        {/* Header */}
        <div
          ref={ref}
          className="text-center mb-16"
        >
          <span className="text-accent-600 font-semibold text-sm uppercase tracking-wide">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-primary-800 mt-4 mb-6">
            Let's Plan Your
            <span className="text-accent-600"> Dream Journey</span>
          </h2>
          <p className="text-xl text-primary-600 max-w-3xl mx-auto">
            Ready to hit the road? Contact us for bookings, inquiries, or custom itineraries
          </p>
        </div>

        <div
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
        >
          {contactInfo.map((info, index) => (
            <a
              key={index}
              href={info.link}
              target={info.link.startsWith('http') ? '_blank' : undefined}
              rel={info.link.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all text-center group"
            >
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br ${info.color} text-white flex items-center justify-center text-2xl group-hover:scale-110 transition-transform`}>
                {info.icon}
              </div>
              <h4 className="font-bold text-primary-800 mb-2">{info.title}</h4>
              <p className="text-primary-600 text-sm">{info.details}</p>
            </a>
          ))}
        </div>

        {/* Contact Form & Info */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <div className="transition-all duration-300">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
              <h3 className="text-3xl font-bold text-primary-800 mb-6">
                Book Your Caravan
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-primary-700 font-semibold mb-2">
                    <FaUser className="inline mr-2" />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                    placeholder="Enter your name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-primary-700 font-semibold mb-2">
                      <FaEnvelope className="inline mr-2" />
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-primary-700 font-semibold mb-2">
                      <FaPhone className="inline mr-2" />
                      Phone *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                      placeholder="+91 xxxxx xxxxx"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-primary-700 font-semibold mb-2">
                      <FaUsers className="inline mr-2" />
                      Number of Travelers
                    </label>
                    <select
                      name="travelers"
                      value={formData.travelers}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                    >
                      <option value="">Select...</option>
                      <option value="1-2">1-2 People</option>
                      <option value="3-4">3-4 People</option>
                      <option value="5-6">5-6 People</option>
                      <option value="7-9">7-9 People</option>
                      <option value="10+">10+ People</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-primary-700 font-semibold mb-2">
                      <FaCalendarAlt className="inline mr-2" />
                      Travel Dates
                    </label>
                    <input
                      type="text"
                      name="dates"
                      value={formData.dates}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                      placeholder="e.g., March 15-22"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-primary-700 font-semibold mb-2">
                    <FaMapMarkerAlt className="inline mr-2" />
                    Preferred Destination
                  </label>
                  <input
                    type="text"
                    name="destination"
                    value={formData.destination}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors"
                    placeholder="e.g., Goa, Rajasthan, Himalayas"
                  />
                </div>

                <div>
                  <label className="block text-primary-700 font-semibold mb-2">
                    Additional Requirements
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-3 rounded-xl border-2 border-primary-200 focus:border-accent-500 focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your trip requirements..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full btn-primary flex items-center justify-center space-x-2"
                >
                  <FaPaperPlane />
                  <span>Send Inquiry via WhatsApp</span>
                </button>
              </form>
            </div>
          </div>

          {/* Info & Map */}
          <div
            className="space-y-8"
          >
            {/* Office Info */}
            <div className="bg-gradient-to-br from-primary-600 to-forest-600 rounded-3xl p-8 md:p-12 text-white">
              <h3 className="text-3xl font-bold mb-6">Visit Our Office</h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <FaMapMarkerAlt className="text-2xl text-accent-300 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Address</h4>
                    <p className="text-primary-100">
                      Vishram, 158 B - hilltop Layout,<br />
                      Verma layout, Ambazari,<br />
                      Nagpur, Maharashtra - 440033
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <FaPhone className="text-2xl text-accent-300 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Phone</h4>
                    <p className="text-primary-100">+91 83808-66111</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <FaEnvelope className="text-2xl text-accent-300 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold mb-1">Email</h4>
                    <p className="text-primary-100">info@wowcaravans.com</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h4 className="text-2xl font-bold text-primary-800 mb-6">Business Hours</h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-primary-600">Monday - Saturday</span>
                  <span className="font-semibold text-primary-800">9:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-primary-600">Sunday</span>
                  <span className="font-semibold text-primary-800">10:00 AM - 6:00 PM</span>
                </div>
              </div>
              <div className="mt-6 p-4 bg-accent-50 rounded-xl">
                <p className="text-sm text-primary-700">
                  <strong>Note:</strong> 24/7 WhatsApp support available for urgent queries
                </p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-accent-400 to-accent-600 rounded-2xl p-6 text-white text-center">
                <div className="text-3xl font-bold mb-1">&lt; 24hrs</div>
                <div className="text-sm opacity-90">Response Time</div>
              </div>
              <div className="bg-gradient-to-br from-forest-400 to-forest-600 rounded-2xl p-6 text-white text-center">
                <div className="text-3xl font-bold mb-1">100%</div>
                <div className="text-sm opacity-90">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
